import { useState } from 'react';
import { Check, UserPlus, X } from 'lucide-react';
import { Alert, Avatar, Badge, Button, Card, CardBody, CardHeader } from '@tutor/ui';
import { ApiError } from '../lib/api-client';
import { useReceivedInvitations, useRespondToInvitation } from '../hooks/queries';

/**
 * Invitations waiting for the signed-in student.
 *
 * Renders nothing when there are none, so it can sit at the top of the dashboard
 * without taking space in the common case.
 */
export function PendingInvitations() {
  const { data: invitations } = useReceivedInvitations();
  const respond = useRespondToInvitation();
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!invitations || invitations.length === 0) return null;

  const onRespond = async (id: string, action: 'accept' | 'decline'): Promise<void> => {
    setError(null);
    setBusyId(id);
    try {
      await respond.mutateAsync({ id, action });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not record your response.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="mb-6 border-brand-200 bg-brand-50/40">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-brand-600" aria-hidden />
            {invitations.length === 1 ? 'A tutor wants to connect' : 'Tutors want to connect'}
          </span>
        }
        description="Accepting lets them schedule lessons and set you homework."
      />
      <CardBody className="space-y-3">
        {error && <Alert tone="danger">{error}</Alert>}

        {invitations.map((invitation) => (
          <div
            key={invitation.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3"
          >
            <Avatar name={invitation.tutor.fullName} src={invitation.tutor.avatarUrl} size="md" />

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate font-medium text-slate-900">{invitation.tutor.fullName}</p>
                {invitation.subject && <Badge tone="brand">{invitation.subject.name}</Badge>}
              </div>
              {invitation.tutor.headline && (
                <p className="truncate text-sm text-slate-500">{invitation.tutor.headline}</p>
              )}
              {invitation.message && (
                <p className="mt-1 text-sm italic text-slate-600">“{invitation.message}”</p>
              )}
            </div>

            <div className="ml-auto flex shrink-0 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void onRespond(invitation.id, 'decline')}
                disabled={busyId !== null}
                leftIcon={<X className="h-4 w-4" aria-hidden />}
              >
                Decline
              </Button>
              <Button
                size="sm"
                onClick={() => void onRespond(invitation.id, 'accept')}
                isLoading={busyId === invitation.id && respond.isPending}
                disabled={busyId !== null}
                leftIcon={<Check className="h-4 w-4" aria-hidden />}
              >
                Accept
              </Button>
            </div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
